import { Dialog as SheetPrimitive } from '@base-ui/react/dialog';
import { cva, type VariantProps } from 'class-variance-authority';
import * as React from 'react';

import { Button, ButtonVariant } from '@ui/components/base/button';
import { XIcon } from '@ui/components/icons';
import { cn } from '@ui/lib/utils';
import { ZIndex } from '@ui/zIndexUtils';

type SheetProps = SheetPrimitive.Root.Props;

const Sheet = ({ ...props }: SheetProps) => <SheetPrimitive.Root data-slot="sheet" {...props} />;

type SheetTriggerProps = SheetPrimitive.Trigger.Props;

const SheetTrigger = ({ ...props }: SheetTriggerProps) => (
  <SheetPrimitive.Trigger data-slot="sheet-trigger" {...props} />
);

type SheetPortalProps = SheetPrimitive.Portal.Props;

const SheetPortal = ({ ...props }: SheetPortalProps) => <SheetPrimitive.Portal data-slot="sheet-portal" {...props} />;

type SheetCloseProps = SheetPrimitive.Close.Props;

const SheetClose = ({ ...props }: SheetCloseProps) => <SheetPrimitive.Close data-slot="sheet-close" {...props} />;

type SheetOverlayProps = SheetPrimitive.Backdrop.Props;

const SheetOverlay = ({ className, ...props }: SheetOverlayProps) => (
  <SheetPrimitive.Backdrop
    data-slot="sheet-overlay"
    className={cn(
      'fixed inset-0 isolate z-50 bg-black/10 duration-200',
      'supports-backdrop-filter:backdrop-blur-xs',
      'data-open:animate-in data-open:fade-in-0',
      'data-closed:animate-out data-closed:fade-out-0 data-closed:fill-mode-forwards',
      className
    )}
    {...props}
  />
);

const sheetContentVariants = cva(
  cn(
    'fixed inset-y-0 z-50 flex h-full w-full flex-col bg-popover text-sm text-popover-foreground',
    'shadow-elevation-3 ring-1 ring-foreground/10 duration-200 outline-none',
    'data-open:animate-in data-closed:animate-out'
  ),
  {
    variants: {
      side: {
        right: 'right-0 data-open:slide-in-from-right data-closed:slide-out-to-right',
        left: 'left-0 data-open:slide-in-from-left data-closed:slide-out-to-left',
      },
    },
    defaultVariants: { side: 'right' },
  }
);

interface SheetContentProps extends SheetPrimitive.Popup.Props, VariantProps<typeof sheetContentVariants> {
  modal?: boolean;
  showCloseButton?: boolean;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
  zIndex?: number;
}

const SheetContent = ({
  className,
  children,
  side,
  modal = true,
  showCloseButton = true,
  style,
  zIndex = ZIndex.DIALOG,
  ...props
}: SheetContentProps) => (
  <SheetPortal>
    {modal && <SheetOverlay style={{ zIndex }} />}
    <SheetPrimitive.Popup
      data-slot="sheet-content"
      style={{ zIndex, ...style }}
      className={cn(sheetContentVariants({ side }), className)}
      {...props}
    >
      {children}
      {showCloseButton && (
        <SheetPrimitive.Close
          data-slot="sheet-close"
          render={<Button variant={ButtonVariant.GHOST} className="absolute top-3 right-3" size="icon-sm" />}
        >
          <XIcon />
          <span className="sr-only">Close</span>
        </SheetPrimitive.Close>
      )}
    </SheetPrimitive.Popup>
  </SheetPortal>
);

type SheetHeaderProps = React.ComponentProps<'div'>;

const SheetHeader = ({ className, ...props }: SheetHeaderProps) => (
  <div
    data-slot="sheet-header"
    className={cn('flex flex-col gap-1.5 border-b border-border p-4', className)}
    {...props}
  />
);

type SheetFooterProps = React.ComponentProps<'div'>;

const SheetFooter = ({ className, ...props }: SheetFooterProps) => (
  <div
    data-slot="sheet-footer"
    className={cn('mt-auto flex flex-wrap items-center gap-2 border-t border-border p-4', className)}
    {...props}
  />
);

type SheetTitleProps = SheetPrimitive.Title.Props;

const SheetTitle = ({ className, ...props }: SheetTitleProps) => (
  <SheetPrimitive.Title
    data-slot="sheet-title"
    className={cn('text-base leading-none font-medium', className)}
    {...props}
  />
);

type SheetDescriptionProps = SheetPrimitive.Description.Props;

const SheetDescription = ({ className, ...props }: SheetDescriptionProps) => (
  <SheetPrimitive.Description
    data-slot="sheet-description"
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
);

export {
  Sheet,
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetOverlay,
  SheetPortal,
  SheetTitle,
  SheetTrigger,
};
